import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clubAPI, userAPI, uploadAPI } from '../services/api';
import MapPicker from '../components/MapPicker';
import { useAlert } from '../components/Alert';
import './ClubPage.css';

const ClubPage = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    loadClub();
  }, [clubId]);

  const loadClub = async () => {
    try {
      setLoading(true);
      
      // Load club data and members in parallel for better performance
      const [clubResponse, membersResponse] = await Promise.all([
        clubAPI.getClub(clubId),
        clubAPI.getClubMembers(clubId, 100), // Load first 100 members (can add pagination later if needed)
      ]);
      
      setClub(clubResponse.data);
      setMembers(membersResponse.data.members || []);
      
      // TODO: If membersResponse.data.hasMore is true, could add "Load More" button
      // or implement infinite scroll for clubs with 100+ members
    } catch (err) {
      console.error('Load club error:', err);
      setError('Failed to load club');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = () => {};

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newMemberEmail.trim()) {
      showAlert('Please enter an email', 'warning');
      return;
    }

    setAddingMember(true);
    setError('');

    try {
      await clubAPI.addMember(clubId, newMemberEmail.trim());
      setNewMemberEmail('');
      await loadClub();
    } catch (err) {
      console.error('Add member error:', err);
      const errorMessage = err.response?.data?.error || 'Failed to add member';
      if (errorMessage === 'User not found with that email') {
        showAlert('User not found with that email', 'warning');
      } else {
        setError(errorMessage);
      }
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member from the club?')) {
      return;
    }

    try {
      await clubAPI.removeMember(clubId, userId);
      await loadClub();
    } catch (err) {
      console.error('Remove member error:', err);
      setError(err.response?.data?.error || 'Failed to remove member');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!club) {
    return (
      <div className="page-container">
        <div className="container">
          <div className="error-message">Club not found</div>
        </div>
      </div>
    );
  }

  const canEdit = club.isAdmin || club.isGlobalAdmin;

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <button
            onClick={() => navigate(-1)}
            className="btn btn-secondary"
            style={{ marginBottom: '16px' }}
          >
            ← Back
          </button>
          <h1 className="page-title">{club.name}</h1>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="club-details">
          <div className="card">
            <div className="card-header">
              <h2>Club Information</h2>
              {canEdit && (
                <button
                  onClick={() => navigate(`/clubs/${clubId}/edit`)}
                  className="btn btn-secondary"
                >
                  Edit
                </button>
              )}
            </div>

            <div className="club-info">
              {club.logo && (
                <div className="club-logo">
                  <img src={club.logo} alt={`${club.name} logo`} />
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Club Name</span>
                <span className="info-value">{club.name}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Sport</span>
                <span className="info-value sport-badge">
                  {(club.sport === 'Tennis (singles)' || club.sport === 'Tennis (doubles)') && '🎾'}
                  {club.sport === 'Padel' && '🏓'}
                  {club.sport === 'Squash' && '🏸'}
                  {club.sport === 'Football' && '⚽'}
                  {club.sport === 'Basketball' && '🏀'}
                  {' '}{club.sport || 'Tennis (singles)'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Members</span>
                <span className="info-value">{members.length}</span>
              </div>
            </div>

            {club.latitude && club.longitude && (
              <div className="club-map-container">
                <MapPicker
                  initialLocation={{
                    lat: parseFloat(club.latitude),
                    lng: parseFloat(club.longitude),
                  }}
                  readOnly={true}
                  height="350px"
                />
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h2>Members</h2>
              {canEdit && (
                <form onSubmit={handleAddMember} className="add-member-form">
                  <input
                    type="email"
                    className="form-input"
                    style={{ width: '200px', marginRight: '8px' }}
                    value={newMemberEmail}
                    onChange={(e) => setNewMemberEmail(e.target.value)}
                    placeholder="Enter email..."
                    disabled={addingMember}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={addingMember || !newMemberEmail.trim()}
                  >
                    {addingMember ? 'Adding...' : 'Add Member'}
                  </button>
                </form>
              )}
            </div>

            {members.length === 0 ? (
              <div className="empty-state">
                <p>No members yet.</p>
              </div>
            ) : (
              <div className="members-list">
                {members.map((member) => (
                  <div key={member.userId} className="member-item">
                    <div className="member-info">
                      <h3>{member.name || 'Unknown'}</h3>
                      <p className="member-email">{member.email || 'No email'}</p>
                      {club.adminId === member.userId && (
                        <span className="admin-badge">Admin</span>
                      )}
                    </div>
                    {canEdit && club.adminId !== member.userId && (
                      <button
                        onClick={() => handleRemoveMember(member.userId)}
                        className="btn btn-danger"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClubPage;


